import {
  Button,
  OutlinedSelect,
  SearchBox,
  SelectOptionType,
  useMobile,
} from '@octano/global-ui';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';

export interface SearchFiltersType {
  schools: SelectOptionType;
  nameOrCode: string | null;
  modalities: SelectOptionType;
  studyPlanVersions: SelectOptionType;
  academicLevels: SelectOptionType;
}

const defaultValues: SearchFiltersType = {
  schools: (null as unknown) as SelectOptionType,
  nameOrCode: '',
  modalities: (null as unknown) as SelectOptionType,
  studyPlanVersions: (null as unknown) as SelectOptionType,
  academicLevels: (null as unknown) as SelectOptionType,
};

interface SearchFiltersProps {
  onSearch: (filters: SearchFiltersType) => void;
  schools: SelectOptionType[];
  studyPlanVersions: SelectOptionType[];
  modalities: SelectOptionType[];
  academicLevels: SelectOptionType[];
  canCreate?: boolean;
  onCreate?: () => void;
}

const SearchFilters = ({
  onSearch,
  schools,
  studyPlanVersions,
  modalities,
  academicLevels,
  canCreate,
  onCreate,
}: SearchFiltersProps) => {
  const prefix = 'filters';
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_LIST);
  const { control, handleSubmit, setValue, reset } = useForm<SearchFiltersType>(
    { defaultValues },
  );
  const isMobile = useMobile();

  const onSubmit = (values: SearchFiltersType) => {
    onSearch(values);
  };

  // HU 15447 — Task B16 (A2): botón LIMPIAR resetea el form y dispara una
  // búsqueda vacía para que el listado también se limpie. Espejo del patrón
  // existente en web-aprendizaje/classesBook/SearchControls.handleClear.
  const handleClear = useCallback(() => {
    reset(defaultValues);
    onSearch(defaultValues);
  }, [reset, onSearch]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row className="pb-4 gutters-filters">
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="schools"
            options={schools}
            label={t(`${prefix}.schools`)}
            placeholder={t(`${prefix}.placeholderSchools`)}
            control={control}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <SearchBox
            name="nameOrCode"
            label={t(`${prefix}.nameOrCode`)}
            placeholder={t(`${prefix}.placeholderNameOrCode`)}
            control={control}
            clear={() => setValue('nameOrCode', '')}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="academicLevels"
            options={academicLevels}
            label={t(`${prefix}.academicLevels`)}
            placeholder={t(`${prefix}.placeholderAcademicLevels`)}
            control={control}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="studyPlanVersions"
            options={studyPlanVersions}
            label={t(`${prefix}.studyPlanVersions`)}
            placeholder={t(`${prefix}.placeholderStudyPlanVersions`)}
            control={control}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="modalities"
            options={modalities}
            label={t(`${prefix}.modalities`)}
            placeholder={t(`${prefix}.placeholderModalities`)}
            control={control}
            isClearable
          />
        </Col>
        <Col
          xs={12}
          md={{ offset: 6, size: 3 }}
          style={{ marginTop: isMobile ? 0 : '1.5rem' }}
        >
          <div className="d-flex flex-column" style={{ gap: 24 }}>
            <div
              className="d-flex w-100 align-items-center h-100"
              style={{ gap: 8 }}
            >
              <Button
                type="submit"
                text={t(`common:actions.search`)}
                size="md"
                className="flex-fill"
              />
              <Button
                type="button"
                text={t(`common:actions.clean`)}
                size="md"
                outlined
                className="flex-fill"
                onClick={handleClear}
              />
            </div>
            {canCreate && onCreate && (
              <Button
                type="button"
                icon="plus"
                text={t('btnNewCourse')}
                size="md"
                className="flex-fill"
                onClick={onCreate}
              />
            )}
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default SearchFilters;
